import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { Loader2, ArrowLeft, FileText, ExternalLink } from 'lucide-react';

const STATUS_COLORS = {
  sent: 'bg-green-500/20 text-green-400 border-green-500/30',
  pending: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  failed: 'bg-red-500/20 text-red-400 border-red-500/30',
  not_required: 'bg-white/10 text-gray-200 border-white/20',
};

const TYPE_LABELS = {
  project: 'Single Project',
  multi_project: 'Multi-Project',
  general: 'General Overhead',
};

function formatUKDate(isoStr) {
  if (!isoStr) return '-';
  try {
    const d = new Date(isoStr);
    const dd = d.getDate().toString().padStart(2, '0');
    const mm = (d.getMonth() + 1).toString().padStart(2, '0');
    const yyyy = d.getFullYear();
    const hh = d.getHours().toString().padStart(2, '0');
    const min = d.getMinutes().toString().padStart(2, '0');
    return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
  } catch {
    return isoStr.slice(0, 16);
  }
}

export function MyCaptureDetail() {
  const { captureId } = useParams();
  const { api } = useAuth();
  const navigate = useNavigate();
  const [capture, setCapture] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get(`/captures/${captureId}`);
        setCapture(res.data);
      } catch {
        toast.error('Capture not found');
        navigate(-1);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [captureId, api, navigate]);

  if (loading) {
    return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#F5A623]" /></div>;
  }

  if (!capture) return null;

  const c = capture;
  const companyName = c.company === 'DEV' ? 'Lux Developments' : 'Lux Construction';
  const pdfUrl = c.output_pdf?.onedrive_url;

  return (
    <div className="p-4 pb-28 max-w-2xl mx-auto" data-testid="capture-detail-page">
      {/* Back */}
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-gray-400 hover:text-white mb-4 text-sm" data-testid="back-btn">
        <ArrowLeft className="w-4 h-4" /> Back to My Captures
      </button>

      {/* PDF preview */}
      <Card className="bg-[#2A3240] border-white/10 mb-4">
        <CardContent className="p-4 flex items-center gap-4">
          <div className="w-16 h-16 rounded-lg bg-[#1E2530] border border-white/10 flex items-center justify-center flex-shrink-0">
            <FileText className="w-8 h-8 text-red-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white text-sm font-medium truncate">{c.output_pdf?.filename || 'receipt.pdf'}</p>
            <p className="text-gray-400 text-xs">
              {c.output_pdf?.page_count || 1} page{(c.output_pdf?.page_count || 1) > 1 ? 's' : ''}
              {c.output_pdf?.size_bytes ? ` · ${Math.round(c.output_pdf.size_bytes / 1024)}KB` : ''}
            </p>
          </div>
          {pdfUrl && (
            <a href={pdfUrl} target="_blank" rel="noopener noreferrer" className="text-[#F5A623] text-xs hover:underline flex items-center gap-1 flex-shrink-0">
              <ExternalLink className="w-3.5 h-3.5" /> View
            </a>
          )}
        </CardContent>
      </Card>

      {/* Status banner */}
      <div className={`rounded-lg border p-3 mb-4 ${STATUS_COLORS[c.sent_status]}`} data-testid="status-banner">
        {c.sent_status === 'not_required' && (
          <div>
            <p className="text-sm font-medium">Details only — logged to the job, nothing sent to LiveCosts</p>
            {c.cost?.description && <p className="text-xs mt-1 opacity-80">{c.cost.description}</p>}
            <p className="text-xs mt-1 opacity-70">The supplier's invoice will cover this entry when it's posted from the Cost Inbox.</p>
          </div>
        )}
        {c.sent_status === 'sent' && (
          <div>
            <p className="text-sm">Sent successfully to {c.destination_type} on {formatUKDate(c.sent_at)}</p>
            {c.destination_email && (
              <p className="text-xs mt-1 opacity-70" data-testid="sent-to-line">Sent to: {c.destination_email}</p>
            )}
            {c.bcc_email && (
              <p className="text-xs opacity-70" data-testid="bcc-line">BCC: {c.bcc_email}</p>
            )}
          </div>
        )}
        {c.sent_status === 'pending' && (
          <p className="text-sm">Send pending...</p>
        )}
        {c.sent_status === 'failed' && (
          <div>
            <p className="text-sm font-medium">Send failed</p>
            {c.failure_reason && <p className="text-xs mt-1 opacity-80">{c.failure_reason}</p>}
            <p className="text-xs mt-2 text-red-300/70" data-testid="resend-instruction">
              To resend, please re-upload the original receipt.
            </p>
          </div>
        )}
      </div>

      {/* Metadata */}
      <Card className="bg-[#2A3240] border-white/10">
        <CardContent className="p-4 space-y-3 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-400">Type</span>
            <span className="text-white">{TYPE_LABELS[c.capture_type] || c.capture_type}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">Company</span>
            <Badge className={`${c.company === 'CON' ? 'bg-blue-500/20 text-blue-400' : 'bg-orange-500/20 text-orange-400'}`}>
              {companyName}
            </Badge>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">Destination</span>
            <span className="text-white capitalize">{c.destination_type}</span>
          </div>
          {c.capture_type === 'project' && c.linked_project_ref && (
            <div className="flex justify-between">
              <span className="text-gray-400">Project</span>
              <span className="text-[#F5A623]">{c.linked_project_ref} {c.linked_project_name || ''}</span>
            </div>
          )}
          {c.notes && (
            <div className="flex justify-between">
              <span className="text-gray-400">Notes</span>
              <span className="text-white text-right max-w-[60%]">{c.notes}</span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-gray-400">Sent To</span>
            <span className="text-white">{c.destination_email || '-'}</span>
          </div>
          {c.sent_at && (
            <div className="flex justify-between">
              <span className="text-gray-400">Sent At</span>
              <span className="text-white">{formatUKDate(c.sent_at)}</span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-gray-400">Created At</span>
            <span className="text-white">{formatUKDate(c.created_at)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">Created By</span>
            <span className="text-white">{c.user_name || '-'}</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
